import { type RefObject, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
	"a[href]",
	"button:not([disabled])",
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	"[tabindex]:not([tabindex='-1'])"
].join(",");

/**
 * Open dialogs, deepest last. Only the top entry traps Tab, so a confirmation layered over
 * another dialog keeps the keyboard inside itself instead of both fighting for it.
 */
const openDialogs: HTMLElement[] = [];

/** Skips controls that are present but not rendered (a collapsed section, a hidden step). */
const isVisible = (element: HTMLElement): boolean =>
	element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;

/**
 * Keeps keyboard focus inside a dialog while it is open and hands focus back when it closes.
 *
 * On open, focus moves to the dialog element itself rather than its first control, so a screen
 * reader announces the dialog's name before its contents. Tab and Shift+Tab then cycle within
 * the dialog, and on close focus returns to whatever was focused before it opened — usually the
 * control that triggered it.
 *
 * Attach the returned ref to the element carrying `role="dialog"` (or `role="alertdialog"`).
 */
export const useDialogFocus = <T extends HTMLElement>(): RefObject<T> => {
	const dialogRef = useRef<T>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return undefined;
		}

		const previouslyFocused = document.activeElement as HTMLElement | null;
		openDialogs.push(dialog);

		// Focusable without joining the tab sequence, so Tab from here lands on the first control.
		dialog.setAttribute("tabindex", "-1");
		dialog.focus();

		const onKeyDown = (event: KeyboardEvent): void => {
			// Only the innermost open dialog traps; the ones beneath it stay passive.
			if (event.key !== "Tab" || openDialogs[openDialogs.length - 1] !== dialog) {
				return;
			}

			const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
			if (focusable.length === 0) {
				// Nothing to move to - keep focus on the dialog rather than letting it escape.
				event.preventDefault();

				return;
			}

			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement;
			const hasFocusInside = active instanceof Node && dialog.contains(active);

			if (event.shiftKey && (active === first || !hasFocusInside)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && (active === last || !hasFocusInside)) {
				event.preventDefault();
				first.focus();
			}
		};

		// Capture phase so the trap runs before anything inside the dialog handles Tab.
		document.addEventListener("keydown", onKeyDown, true);

		return () => {
			document.removeEventListener("keydown", onKeyDown, true);

			const index = openDialogs.indexOf(dialog);
			if (index !== -1) {
				openDialogs.splice(index, 1);
			}

			// Skip restoring to an element that has since left the document (e.g. the trigger was
			// in a list the dialog just changed), which would otherwise drop focus onto <body>.
			if (previouslyFocused && document.contains(previouslyFocused)) {
				previouslyFocused.focus();
			}
		};
	}, []);

	return dialogRef;
};
